import {
  useMarkAllRead,
  useMarkRead,
  useRecentNotifications,
  useUnreadCount,
} from '@/hooks/useNotifications'
import { cn } from '@/lib/cn'
import { formatRelativeDate } from '@/lib/formatters'
import type { NotificationDTO, Severity } from '@/lib/notify'
import { useNavigate } from '@tanstack/react-router'
import { AnimatePresence, motion } from 'framer-motion'
import {
  AlertOctagon,
  AlertTriangle,
  Bell,
  CheckCheck,
  CheckCircle2,
  Info,
  type LucideIcon,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

const SEVERITY: Record<Severity, { icon: LucideIcon; ring: string; text: string; bg: string }> = {
  info: { icon: Info, ring: 'ring-blue-500/20', text: 'text-blue-600 dark:text-blue-400', bg: 'bg-blue-500/10' },
  success: {
    icon: CheckCircle2,
    ring: 'ring-emerald-500/20 dark:ring-emerald-400/20',
    text: 'text-emerald-600 dark:text-emerald-400',
    bg: 'bg-emerald-500/10',
  },
  warning: {
    icon: AlertTriangle,
    ring: 'ring-amber-500/20 dark:ring-amber-400/20',
    text: 'text-amber-600 dark:text-amber-400',
    bg: 'bg-amber-500/10',
  },
  critical: {
    icon: AlertOctagon,
    ring: 'ring-red-500/20 dark:ring-red-400/20',
    text: 'text-red-600 dark:text-red-400',
    bg: 'bg-red-500/10',
  },
}

export function NotificationBell() {
  const { t, i18n } = useTranslation('common')
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  const { data: unread = 0 } = useUnreadCount()
  const { data: items = [], isLoading } = useRecentNotifications(false, 20)
  const markRead = useMarkRead()
  const markAll = useMarkAllRead()

  const locale = i18n.language === 'en' ? 'en' : 'fr'

  useEffect(() => {
    if (!open) return
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const handleOpen = (n: NotificationDTO) => {
    if (!n.read_at) markRead.mutate(n.id)
    setOpen(false)
    if (n.href) navigate({ to: n.href })
  }

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={t('notifications.title', 'Notifications')}
        aria-expanded={open}
        className={cn(
          'relative flex h-9 w-9 items-center justify-center rounded-xl border border-border text-muted-foreground transition-colors hover:bg-muted hover:text-foreground',
          open && 'bg-muted text-foreground',
        )}
      >
        <Bell className="h-[18px] w-[18px]" />
        {unread > 0 ? (
          <>
            <span className="absolute -right-1 -top-1 inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-accent px-1 text-[10px] font-bold leading-none text-accent-foreground ring-2 ring-header-background">
              {unread > 99 ? '99+' : unread}
            </span>
            <span className="absolute -right-1 -top-1 h-[18px] w-[18px] animate-ping rounded-full bg-accent/50" />
          </>
        ) : null}
      </button>

      <AnimatePresence>
        {open ? (
        <motion.div
          initial={{ opacity: 0, y: -4, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -4, scale: 0.97 }}
          transition={{ duration: 0.15 }}
          className="absolute right-0 z-50 mt-2 w-[380px] max-w-[calc(100vw-2rem)] overflow-hidden rounded-2xl border border-border bg-card shadow-2xl">
          {/* Header */}
          <div className="flex items-center justify-between gap-2 border-b border-border px-4 py-3">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-foreground">
                {t('notifications.title', 'Notifications')}
              </span>
              {unread > 0 ? (
                <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-accent px-1.5 text-[11px] font-bold text-accent-foreground">
                  {unread}
                </span>
              ) : null}
            </div>
            {unread > 0 ? (
              <button
                type="button"
                onClick={() => markAll.mutate()}
                disabled={markAll.isPending}
                className="flex items-center gap-1.5 rounded-lg px-2 py-1 text-xs font-medium text-primary transition-colors hover:bg-primary/10 disabled:opacity-50"
              >
                <CheckCheck className="h-3.5 w-3.5" />
                {t('notifications.mark_all_read', 'Tout marquer lu')}
              </button>
            ) : null}
          </div>

          {/* List */}
          <div className="max-h-[min(70vh,460px)] overflow-y-auto">
            {isLoading ? (
              <div className="flex flex-col gap-2 p-3">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="h-14 animate-pulse rounded-xl bg-muted" />
                ))}
              </div>
            ) : items.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-2 px-6 py-12 text-center">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                  <Bell className="h-5 w-5 text-muted-foreground" />
                </div>
                <p className="text-sm font-medium text-foreground">
                  {t('notifications.empty_title', 'Aucune notification')}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t('notifications.empty_desc', 'Vous êtes à jour.')}
                </p>
              </div>
            ) : (
              <ul className="divide-y divide-border">
                {items.map((n) => {
                  const sev = SEVERITY[n.severity] ?? SEVERITY.info
                  const Icon = sev.icon
                  const isUnread = !n.read_at
                  return (
                    <li key={n.id}>
                      <button
                        type="button"
                        onClick={() => handleOpen(n)}
                        className={cn(
                          'flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/60',
                          isUnread && 'bg-primary/[0.04]',
                        )}
                      >
                        <span
                          className={cn(
                            'mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full ring-1',
                            sev.bg,
                            sev.ring,
                          )}
                        >
                          <Icon className={cn('h-4 w-4', sev.text)} />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="flex items-center gap-2">
                            <span
                              className={cn(
                                'truncate text-sm',
                                isUnread
                                  ? 'font-semibold text-foreground'
                                  : 'font-medium text-foreground/80',
                              )}
                            >
                              {t(n.title_key, n.body_params)}
                            </span>
                            {isUnread ? (
                              <span className="ml-auto h-2 w-2 shrink-0 rounded-full bg-accent" />
                            ) : null}
                          </span>
                          <span className="mt-0.5 line-clamp-2 block text-xs text-muted-foreground">
                            {t(n.body_key, n.body_params)}
                          </span>
                          <span className="mt-1 block text-[11px] text-muted-foreground/70">
                            {formatRelativeDate(n.created_at, locale)}
                          </span>
                        </span>
                      </button>
                    </li>
                  )
                })}
              </ul>
            )}
          </div>
        </motion.div>
      ) : null}
      </AnimatePresence>
    </div>
  )
}
